// Updated TestTypes.ts - Removed connection testing, updated for account configuration

import { SampleResult } from "../lab/SampleResultTypes";
import { Patient } from "./PatientTypes";
import { BloodTest } from "../lab/BloodTestTypes";
import type { UserUuid } from "../identity";

export interface CustomTestPackage {
	id: number;
	name: string;
	value: string;
	description?: string;
	analysis_types: string[];
	test_category_id?: number;
	is_active: boolean;
	creates_lab_record: boolean;
	requires_fasting: boolean;
	requires_contrast: boolean;
	specimen_types: string[] | null;
	form_fields: FormField[] | null;
	sort_order: number;
	created_at?: string;
	updated_at?: string;
	test_category?: TestCategory;
	analysis_type_details?: TestType[];
	is_global?: boolean; // Indicates if the package is managed by the global admin system
}

export interface Test {
	id: number;
	patient_id: UserUuid;
	test_type: string[];
	test_category: string;
	test_name?: string;
	custom_test_package_ids?: number[]; // New field for multiple packages
	custom_test_packages?: CustomTestPackage[]; // New field for multiple packages
	lab_order_id?: number;
	ordering_provider_id?: UserUuid;
	ordering_provider?: {
		id: UserUuid;
		first_name: string;
		last_name: string;
	};
	facility?: string;
	sample_id?: string;
	priority: string;
	status: string;
	reason?: string;
	billable_diagnosis?: string;
	details?: string | any;
	sample_type?: string;
	created_at?: string;
	updated_at?: string;
	/** Server-computed: MAX(GREATEST(results_available_date, updated_at)) across the order's results. */
	results_updated_at?: string;
	test_date?: Date | null;
	scheduled_date?: Date | null;
	completed_date?: Date | null;
	patient?: Patient;
	result?: SampleResult;
	blood_test_id?: number;
	report_file_id?: string; // file-service id of the original uploaded report PDF
	bloodTest?: BloodTest;
	/** Laravel serialises the relation snake_cased; `bloodTest` is never present in API JSON. */
	blood_test?: BloodTest;
	test_results?: TestResult[]; // New relationship to individual test results
	// Used only during form submission – not persisted on the model directly
	send_to_lab?: boolean;
}

// ---------------------------------------------------------------------------
// Printable pathology request slip (ClickUp 86d3x1x66 / S3)
//
// The response shape of POST /patients/test-orders/print-data. Hand-mirrored from the
// backend's explicit whitelist in TestOrderPrintDataService — there is no generated
// contract, so if that whitelist changes, this changes in the same commit.
// ---------------------------------------------------------------------------

/** One requested test, resolved from its stored slug to a display name. */
export interface PrintableTestEntry {
	value: string;
	name: string;
	category_name: string | null;
}

export interface PrintDataPatient {
	title: string | null;
	first_name: string;
	middle_name: string | null;
	last_name: string;
	date_of_birth: string | null;
	sex: string | null;
	gender: string | null;
	phone: string | null;
	email: string | null;
	address: string | null;
	city: string | null;
	state: string | null;
	postal_code: string | null;
	country: string | null;
	/**
	 * ⚠️ OPTIONAL, NOT NULLABLE-REQUIRED. These three keys are ABSENT from the payload
	 * unless `include_medicare` was requested — the Medicare layout distinguishes
	 * "not requested" from "the patient has none".
	 */
	medicare_number?: string | null;
	medicare_individual_ref_number?: string | null;
	medicare_valid_to?: string | null;
}

export interface PrintDataProvider {
	title: string | null;
	first_name: string;
	last_name: string;
	provider_number: string | null;
	ahpra_registration_number: string | null;
}

export interface PrintDataClinic {
	name: string;
	address: string | null;
	email: string | null;
	phone: string | null;
}

/**
 * ⚠️ `patient`, `ordering_provider` and `clinic` are all nullable, and that is NOT
 * defensive typing. Each is a separate cross-service read that fails soft, and
 * `ordering_provider` is legitimately null for most historical orders. The layouts must
 * render a ruled empty block for each, so the type forces them to handle it.
 */
export interface TestOrderPrintData {
	id: number;
	sample_id: string | null;
	created_at: string | null;
	test_date: string | null;
	priority: string;
	/** Internal lifecycle state — used by the three internal layouts, never by a request slip. */
	status: string;
	details: Record<string, unknown> | null;
	reason: string | null;
	billable_diagnosis: string | null;
	facility: string | null;
	sample_type: string | null;
	tests: PrintableTestEntry[];
	test_count: number;
	custom_test_packages: { value: string; name: string }[];
	requires_fasting: boolean;
	requires_contrast: boolean;
	patient: PrintDataPatient | null;
	ordering_provider: PrintDataProvider | null;
	clinic: PrintDataClinic | null;
}

// TestResult interface - represents individual test results for specific test types
export interface TestResult {
	id: number;
	test_id: number;
	test_type: string;
	results?: any; // The actual results data
	result_notes?: string;
	result_documents?: any;
	is_abnormal: boolean;
	requires_followup: boolean;
	followup_notes?: string;
	status: string; // pending, in_progress, completed, reviewed, etc.
	results_available_date?: string;
	results_reviewed_date?: string;
	created_at: string;
	updated_at: string;
}

// Helper function to normalize test_type to array
export const normalizeTestType = (testType: string | string[] | undefined): string[] => {
	if (!testType) return [];
	if (Array.isArray(testType)) return testType;
	return [testType];
};


export interface TestStats {
	total_orders: number;
	pending_orders: number;
	completed_orders: number;
	abnormal_results: number;
	orders_by_category: Record<string, number>;
	orders_by_status: Record<string, number>;
	orders_requiring_review: number;
}

// Database entity types
export interface TestCategory {
	id: number;
	admin_template_id?: number | null;
	is_global: boolean;
	name: string;
	value: string;
	description?: string;
	is_active: boolean;
	sort_order: number;
	created_at?: string;
	updated_at?: string;
	test_types?: TestType[];
	active_test_types?: TestType[];
	test_types_count?: number;
	active_test_types_count?: number;
}

export interface TestType {
	id: number;
	admin_template_id?: number | null;
	is_global: boolean;
	test_category_id: number;
	name: string;
	value: string;
	description?: string;
	common_fields?: FormField[];
	specific_fields?: FormField[];
	specimen_types?: string[];
	requires_fasting: boolean;
	requires_contrast: boolean;
	is_active: boolean;
	sort_order: number;
	created_at?: string;
	updated_at?: string;
	test_category?: TestCategory;
	form_fields?: FormField[];
}


// Form field types
export interface FormField {
	name: string;
	label: string;
	type: 'text' | 'textarea' | 'date' | 'boolean' | 'number' | 'dropdown' | 'checkbox';
	options?: DropdownOption[];
	required?: boolean;
	placeholder?: string;
	description?: string;
}

export interface DropdownOption {
	label: string;
	value: string;
}


// Priority and status types
export const PRIORITY_OPTIONS: DropdownOption[] = [
	{ label: "Routine", value: "routine" },
	{ label: "Urgent", value: "urgent" },
	{ label: "STAT", value: "stat" }
];

export const STATUS_OPTIONS: DropdownOption[] = [
	{ label: "Ordered", value: "ordered" },
	{ label: "Received At Lab", value: "received_by_lab" },
	{ label: "Scheduled", value: "scheduled" },
	{ label: "Specimen Collected", value: "specimen_collected" },
	{ label: "In Progress", value: "in_progress" },
	{ label: "Completed", value: "completed" },
	{ label: "Cancelled", value: "cancelled" },
	{ label: "Results Available", value: "results_available" },
	{ label: "Results Reviewed", value: "results_reviewed" },
	{ label: "Analysis Failed", value: "analysis_failed" }
];

export const SPECIMEN_TYPE_OPTIONS: DropdownOption[] = [
	{ label: "Blood", value: "blood" },
	{ label: "Urine", value: "urine" },
	{ label: "Stool", value: "stool" },
	{ label: "Saliva", value: "saliva" },
	{ label: "Sputum", value: "sputum" },
	{ label: "Tissue", value: "tissue" },
	{ label: "CSF", value: "csf" },
	{ label: "Other", value: "other" }
];

// Request types for forms (existing)
export interface CreateTestCategoryRequest {
	name: string;
	value: string;
	description?: string;
	is_active?: boolean;
	sort_order?: number;
}

export interface UpdateTestCategoryRequest extends Partial<CreateTestCategoryRequest> { }

export interface CreateTestTypeRequest {
	test_category_id: number;
	name: string;
	value: string;
	description?: string;
	common_fields?: FormField[];
	specific_fields?: FormField[];
	specimen_types?: string[];
	requires_fasting?: boolean;
	requires_contrast?: boolean;
	is_active?: boolean;
	sort_order?: number;
}

export interface UpdateTestTypeRequest extends Partial<CreateTestTypeRequest> { }


export interface BulkUpdateTestTypesRequest {
	test_types: Array<{
		id: number;
		is_active?: boolean;
		sort_order?: number;
	}>;
}

export interface ReorderCategoriesRequest {
	categories: Array<{
		id: number;
		sort_order: number;
	}>;
}

// Search and filter types
export interface TestCategoryFilters {
	active?: boolean;
	include_test_types?: boolean;
	search?: string;
}

export interface TestTypeFilters {
	category?: string;
	active?: boolean;
	search?: string;
	requires_fasting?: boolean;
	requires_contrast?: boolean;
}

// API Response types
export interface ApiResponse<T> {
	success: boolean;
	data: T;
	message: string;
}


export interface CreateCustomTestPackageRequest {
	name: string;
	value?: string;
	description?: string;
	analysis_types: string[];
	is_active?: boolean;
	sort_order?: number;
}

export interface UpdateCustomTestPackageRequest extends Partial<CreateCustomTestPackageRequest> {}

export interface CustomTestPackageFilters {
	category_id?: number;
	active_only?: boolean;
	include_analysis_types?: boolean;
}